import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { RACHEL_PANEL_WIDTH } from '../rachel.layout';
import { RachelDockedLayout } from '../rachel.types';
import { RachelFocusedLabel, RachelPageContext } from '../rachelPageContext.types';

export interface RachelStoreState {
  isOpen: boolean;
  activeChatId: string | null;
  activeChatTeamId: string | null;
  isInputFocused: boolean;
  unreadChatIds: Record<string, boolean>;
  interruptedMessageIds: Record<string, boolean>;
  isCompacting: boolean;
  dockedLayout: RachelDockedLayout;
  focusedLabels: RachelFocusedLabel[];
  dismissedPageContext: RachelPageContext | null;
  open: () => void;
  close: () => void;
  setIsInputFocused: (isInputFocused: boolean) => void;
  setIsCompacting: (isCompacting: boolean) => void;
  setDockedLayout: (dockedLayout: RachelDockedLayout) => void;
  setActiveChatId: (chatId: string, teamId: string | null) => void;
  getActiveChatIdForTeam: (teamId: string | null | undefined) => string | null;
  clearActiveChat: () => void;
  markChatUnread: (chatId: string) => void;
  clearChatUnread: (chatId: string) => void;
  markMessageInterrupted: (messageId: string) => void;
  publishFocusedLabel: (focusedLabel: RachelFocusedLabel) => void;
  unpublishFocusedLabel: (publisherId: string) => void;
  setDismissedPageContext: (pageContext: RachelPageContext | null) => void;
}

export const useRachelStore = create<RachelStoreState>()(
  persist(
    (set, get) => ({
      isOpen: false,
      activeChatId: null,
      activeChatTeamId: null,
      isInputFocused: false,
      unreadChatIds: {},
      interruptedMessageIds: {},
      isCompacting: false,
      dockedLayout: { width: RACHEL_PANEL_WIDTH, groupWidth: 0 },
      focusedLabels: [],
      dismissedPageContext: null,
      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false, dismissedPageContext: null }),
      setIsInputFocused: (isInputFocused) => set({ isInputFocused }),
      setIsCompacting: (isCompacting) => set({ isCompacting }),
      setDockedLayout: (dockedLayout) =>
        set((state) => {
          const { width, groupWidth } = state.dockedLayout;
          if (width === dockedLayout.width && groupWidth === dockedLayout.groupWidth) {
            return state;
          }
          return { dockedLayout };
        }),
      setActiveChatId: (chatId, teamId) => set({ activeChatId: chatId, activeChatTeamId: teamId }),
      getActiveChatIdForTeam: (teamId) => {
        const { activeChatId, activeChatTeamId } = get();
        if (!activeChatId || activeChatTeamId == null || activeChatTeamId !== teamId) {
          return null;
        }
        return activeChatId;
      },
      clearActiveChat: () => set({ activeChatId: null, activeChatTeamId: null }),
      markChatUnread: (chatId) =>
        set((state) => ({ unreadChatIds: { ...state.unreadChatIds, [chatId]: true } })),
      clearChatUnread: (chatId) =>
        set((state) => {
          if (!state.unreadChatIds[chatId]) {
            return state;
          }
          const { [chatId]: _removed, ...rest } = state.unreadChatIds;
          return { unreadChatIds: rest };
        }),
      markMessageInterrupted: (messageId) =>
        set((state) => ({
          interruptedMessageIds: { ...state.interruptedMessageIds, [messageId]: true },
        })),
      publishFocusedLabel: (focusedLabel) =>
        set((state) => {
          const isPublished = state.focusedLabels.some(
            (published) => published.publisherId === focusedLabel.publisherId,
          );
          if (!isPublished) {
            return { focusedLabels: [...state.focusedLabels, focusedLabel] };
          }
          return {
            focusedLabels: state.focusedLabels.map((published) =>
              published.publisherId === focusedLabel.publisherId ? focusedLabel : published,
            ),
          };
        }),
      setDismissedPageContext: (dismissedPageContext) => set({ dismissedPageContext }),
      unpublishFocusedLabel: (publisherId) =>
        set((state) => ({
          focusedLabels: state.focusedLabels.filter(
            (published) => published.publisherId !== publisherId,
          ),
        })),
    }),
    {
      name: 'rachel-store',
      partialize: (state) => ({
        activeChatId: state.activeChatId,
        activeChatTeamId: state.activeChatTeamId,
        dismissedPageContext: state.dismissedPageContext,
      }),
    },
  ),
);

const rachelStore = {
  get isOpen() {
    return useRachelStore.getState().isOpen;
  },
  open: () => useRachelStore.getState().open(),
  close: () => useRachelStore.getState().close(),
  setIsInputFocused: (isInputFocused: boolean) =>
    useRachelStore.getState().setIsInputFocused(isInputFocused),
};

export default rachelStore;
